import { useCallback, useEffect, useRef, useState } from 'react'

export interface SearchHistoryItem {
	query: string
	resultCount: number
	timestamp: number
}

const SEARCH_HISTORY_KEY = 'search_history'
const MAX_HISTORY_ITEMS = 10

export function useSearchHistory() {
	const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([])
	const isInitialized = useRef(false)

	// Function to load search history from localStorage
	const loadSearchHistory = useCallback(() => {
		try {
			if (typeof window !== 'undefined') {
				const stored = localStorage.getItem(SEARCH_HISTORY_KEY)
				if (stored) {
					const parsed = JSON.parse(stored) as SearchHistoryItem[]
					if (Array.isArray(parsed)) {
						setSearchHistory(parsed)
					} else {
						localStorage.removeItem(SEARCH_HISTORY_KEY)
					}
				}
			}
		} catch (error) {
			if (typeof window !== 'undefined') {
				localStorage.removeItem(SEARCH_HISTORY_KEY)
			}
		}
	}, [])

	// Load search history from localStorage on mount
	useEffect(() => {
		if (isInitialized.current) return

		loadSearchHistory()
		isInitialized.current = true
	}, [loadSearchHistory])

	// Listen for storage changes to sync across tabs/windows
	useEffect(() => {
		const handleStorageChange = (e: StorageEvent) => {
			if (e.key === SEARCH_HISTORY_KEY && e.newValue !== null) {
				loadSearchHistory()
			}
		}

		const handleCustomEvent = (e: CustomEvent) => {
			// Defer the state update to avoid "setState during render" warning
			setTimeout(() => {
				setSearchHistory(e.detail.history)
			}, 0)
		}

		if (typeof window !== 'undefined') {
			window.addEventListener('storage', handleStorageChange)
			window.addEventListener(
				'searchHistoryUpdated',
				handleCustomEvent as EventListener,
			)
			return () => {
				window.removeEventListener('storage', handleStorageChange)
				window.removeEventListener(
					'searchHistoryUpdated',
					handleCustomEvent as EventListener,
				)
			}
		}
	}, [loadSearchHistory])

	// Add a new search to history
	const addSearchToHistory = useCallback(
		(query: string, resultCount: number) => {
			if (!query.trim()) return

			setSearchHistory((prev) => {
				// Remove existing entry with same query if it exists
				const filtered = prev.filter(
					(item) => item.query.toLowerCase() !== query.toLowerCase(),
				)

				// Add new entry at the beginning
				const newEntry: SearchHistoryItem = {
					query: query.trim(),
					resultCount,
					timestamp: Date.now(),
				}

				// Keep only the most recent MAX_HISTORY_ITEMS
				const updated = [newEntry, ...filtered].slice(0, MAX_HISTORY_ITEMS)

				// Save to localStorage immediately
				if (typeof window !== 'undefined') {
					localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updated))

					// Dispatch custom event to sync across components in the same window
					window.dispatchEvent(
						new CustomEvent('searchHistoryUpdated', {
							detail: { history: updated },
						}),
					)
				}

				return updated
			})
		},
		[],
	)

	// Clear all search history
	const clearSearchHistory = useCallback(() => {
		setSearchHistory([])
		if (typeof window !== 'undefined') {
			localStorage.removeItem(SEARCH_HISTORY_KEY)
		}
	}, [])

	return {
		searchHistory,
		addSearchToHistory,
		clearSearchHistory,
	}
}
